import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, RefreshCw, Download, Wrench, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ukDate } from "@/lib/utils";

const STATUS_STYLE = {
  change: "bg-amber-100 text-amber-800 border-amber-200",
  needs_review: "bg-red-100 text-red-800 border-red-200",
  match: "bg-emerald-100 text-emerald-800 border-emerald-200",
  applied: "bg-blue-100 text-blue-800 border-blue-200",
  error: "bg-slate-200 text-slate-700",
};
const yn = (v) => (v == null ? "?" : v ? "Yes" : "No");

const ManualFix = ({ row, onSaved }) => {
  const [ho, setHo] = useState(row.home_owner ?? null);
  const [deb, setDeb] = useState(row.debenture ?? false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/loandisk-product-audit/rows/${row.base_sf}`, { home_owner: ho, debenture: deb });
      onSaved(data); toast.success(`${row.base_sf} set to ${data.proposed_product}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const Tog = ({ v, set, testid }) => (
    <span className="inline-flex border rounded overflow-hidden text-[11px]" data-testid={testid}>
      {[["Yes", true], ["No", false]].map(([l, b]) => (
        <button key={l} type="button" onClick={() => set(b)} className={`px-2 py-0.5 ${v === b ? "bg-slate-900 text-white" : "bg-white"}`}>{l}</button>
      ))}
    </span>
  );
  return (
    <div className="flex items-center gap-2 text-[11px]">
      HO <Tog v={ho} set={setHo} testid={`manual-ho-${row.base_sf}`} /> Deb <Tog v={deb} set={setDeb} testid={`manual-deb-${row.base_sf}`} />
      <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={busy || ho == null} onClick={save} data-testid={`manual-save-${row.base_sf}`}>Set</Button>
    </div>
  );
};

export default function LoanDiskProductAudit() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("change,needs_review");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(null);
  const since = "2026-08-01";

  const load = useCallback(async () => {
    const [{ data: s }, { data: r }] = await Promise.all([
      api.get("/loandisk-product-audit/status"),
      api.get("/loandisk-product-audit/rows", { params: filter ? { status: filter } : {} }),
    ]);
    setStatus(s); setRows(r.rows || []);
  }, [filter]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (status?.job?.status !== "running") return undefined;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [status?.job?.status, load]);

  const run = async () => {
    setBusy("run");
    try { await api.post("/loandisk-product-audit/run", null, { params: { region: "UK", since } }); toast.success("Audit started"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); } finally { setBusy(null); }
  };
  const apply = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Update the LoanDisk product on ${selected.size} loan family(ies)? This writes to LoanDisk.`)) return;
    setBusy("apply");
    try {
      const { data } = await api.post("/loandisk-product-audit/apply", { base_sfs: [...selected] }, { params: { region: "UK" } });
      toast[data.failed ? "warning" : "success"](`Applied ${data.applied} · failed ${data.failed}`);
      setSelected(new Set()); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); } finally { setBusy(null); }
  };
  const toggle = (sf) => setSelected((p) => { const n = new Set(p); n.has(sf) ? n.delete(sf) : n.add(sf); return n; });
  const exportCsv = async () => {
    setBusy("csv");
    try {
      const { data } = await api.get("/loandisk-product-audit/export.csv", { params: { region: "UK" }, responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = "LoanDisk_product_audit_UK.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); } finally { setBusy(null); }
  };
  const changeRows = rows.filter((r) => r.status === "change");
  const sum = status?.summary || {};
  const job = status?.job;

  return (
    <div className="p-6 space-y-4" data-testid="ld-product-audit-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">LoanDisk product audit — Homeowner × Debenture</h1>
          <p className="text-sm text-muted-foreground">UK original loans released since {ukDate(since)}. Evidence: Contract Wizard debenture tick → DocuPilot offer SECURITY line → UW INTERNAL note; Homeowner from the UW note, then Monday “Home Owner”.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={run} disabled={busy === "run" || job?.status === "running"} data-testid="audit-run-btn">
            {job?.status === "running" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            {job?.status === "running" ? `Scanning ${job.done}/${job.total}` : "Re-run audit"}
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={busy === "csv"} data-testid="audit-export-btn"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button onClick={apply} disabled={!selected.size || busy === "apply"} className="bg-amber-600 hover:bg-amber-700" data-testid="audit-apply-btn">
            {busy === "apply" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />}Update LoanDisk ({selected.size})
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="audit-summary">
        {["change", "needs_review", "match", "applied", "error"].map((k) => (
          <button key={k} type="button" onClick={() => setFilter(k)} className={`border rounded px-2 py-1 ${STATUS_STYLE[k]} ${filter === k ? "ring-2 ring-slate-400" : ""}`} data-testid={`audit-chip-${k}`}>
            {k.replace("_", " ")}: <b>{sum[k] ?? 0}</b>
          </button>
        ))}
        <button type="button" onClick={() => setFilter("change,needs_review")} className="border rounded px-2 py-1 bg-white" data-testid="audit-chip-actionable">actionable</button>
        <button type="button" onClick={() => setFilter("")} className="border rounded px-2 py-1 bg-white" data-testid="audit-chip-all">all {sum.total ?? 0}</button>
        {changeRows.length > 0 && (
          <button type="button" onClick={() => setSelected(new Set(changeRows.map((r) => r.base_sf)))} className="border rounded px-2 py-1 bg-white underline" data-testid="audit-select-changes">
            select all {changeRows.length} changes
          </button>
        )}
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-xs" data-testid="audit-table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-2 w-6" />
              <th className="p-2">SF / company</th><th className="p-2">Released</th><th className="p-2">Current product</th><th className="p-2">Proposed</th>
              <th className="p-2">HO</th><th className="p-2">Deb</th><th className="p-2">Evidence</th><th className="p-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = r.detail || {};
              return (
                <tr key={r.base_sf} className="border-t align-top hover:bg-slate-50" data-testid={`audit-row-${r.base_sf}`}>
                  <td className="p-2">{r.status === "change" && <Checkbox checked={selected.has(r.base_sf)} onCheckedChange={() => toggle(r.base_sf)} data-testid={`audit-select-${r.base_sf}`} />}</td>
                  <td className="p-2"><div className="font-medium">{r.base_sf}</div><div className="text-muted-foreground truncate max-w-[220px]">{r.company_name}</div>
                    <div className="text-[10px] text-muted-foreground">LD {(r.ld_loan_ids || []).join(", ")}</div></td>
                  <td className="p-2 whitespace-nowrap">{ukDate(r.released_date)}</td>
                  <td className="p-2 font-mono">{r.current_product || r.current_product_id || "—"}</td>
                  <td className="p-2 font-mono font-semibold">{r.proposed_product || <span className="text-red-700 font-sans">unknown</span>}</td>
                  <td className="p-2">{yn(r.home_owner)}<div className="text-[10px] text-muted-foreground">{r.ho_source || "—"}</div></td>
                  <td className="p-2">{yn(r.debenture)}<div className="text-[10px] text-muted-foreground">{r.deb_source || "—"}</div></td>
                  <td className="p-2 text-[10px] text-muted-foreground max-w-[320px]">
                    {d.uw_note?.url ? <a href={d.uw_note.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">UW note<ExternalLink className="w-2.5 h-2.5" /></a> : "no UW note"}
                    {" · "}note HO {yn(d.uw_note?.home_owner)} / Deb {yn(d.uw_note?.debenture)}
                    {d.offer?.security && <> · offer: “{d.offer.security}”</>}
                    {d.offer?.error && !d.offer?.security && <> · offer: {d.offer.error}</>}
                    {d.contract_wizard_debenture != null && <> · wizard Deb {yn(d.contract_wizard_debenture)}</>}
                    {d.monday_home_owner && <> · Monday HO “{d.monday_home_owner}”</>}
                    {(r.conflicts || []).map((c, i) => <div key={i} className="text-red-700">{c}</div>)}
                    {r.apply_error && <div className="text-red-700">{r.apply_error}</div>}
                    {r.status === "needs_review" && <div className="mt-1"><ManualFix row={r} onSaved={(nr) => setRows((p) => p.map((x) => (x.base_sf === nr.base_sf ? nr : x)))} /></div>}
                  </td>
                  <td className="p-2"><Badge variant="outline" className={STATUS_STYLE[r.status]}>{r.status?.replace("_", " ")}</Badge>
                    {r.applied_at && <div className="text-[10px] text-muted-foreground">{ukDate(r.applied_at)}</div>}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">{job ? "No rows for this filter" : "Run the audit to build the list"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
